import { SparePart, SparePartGroup } from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { CategoryItemRow, ContentRow, InputRow, SelectableRow, isSelectableRow } from '@os-components';
import { isSparePart } from '../../features/result/spare-parts-result/spare-parts.helper';
import { PartRow } from './part-table.interface';

/**
 * Creates a unique id for a part. Combination of group, position number and part-id.
 *
 * @param group group name
 * @param part part
 * @param index position number
 * @returns unique id for the part
 */
export const getUniquePartId = (group: SparePartGroup, part: SparePart, index: number) =>
    group.title + '_' + getItemNumber(part, index) + '_' + part.id;

/**
 * Creates a unique id for a group. Combination of group and drive group number.
 *
 * @param group group name
 * @returns unique id for the part
 */
export const getUniqueGroupId = (group: SparePartGroup) =>
    `${group.title}${group.driveGroupNumber ? `_${group.driveGroupNumber}` : ''}`;

/**
 * Get the item/ position number
 *
 * @param part part
 * @param index index of the part based on all parts
 * @returns item/ position number
 */
export const getItemNumber = (part: SparePart, index: number) => part.itemNumber ?? `${index + 1}`;

export const isPartRowIdAndItemNumber = (
    obj: ContentRow<any, any>
): obj is PartRow & {
    rowData: { itemNumber: string; id: string };
    expandData: { itemNumber: string; id: string };
} =>
    obj.rowData !== undefined &&
    obj.expandData !== undefined &&
    typeof obj.rowData === 'object' &&
    obj.rowData.id !== undefined &&
    typeof obj.rowData.id === 'string' &&
    obj.rowData.itemNumber !== undefined &&
    typeof obj.rowData.itemNumber === 'string';

/**
 * Keeps track of all columns which are missing on sew-partner sites.
 * For example: On sew-eurodrive-partner.com, users are not allowed to see the availability column
 * as they are not able to login.
 */
export const columnsMissingOnSEWPartnerSites = ['availability'];

/**
 * Checks if the current website is a sew-partner website. If true, removes forbidden
 * columns from the displayed columns.
 * For example: On sew-eurodrive-partner.com, users are not allowed to see the availability column
 * as they are not able to login.
 *
 * @param countryShortCode country short code (available on HeaderConfigService)
 * @param columns columns to be displayed (without respect to the current website)
 * @returns sanitized columns to display
 *
 */
export const removeSpecificColumnsIfSEWParterSite = (countryShortCode: string, columns: string[]) => {
    if (countryShortCode.toLowerCase() === 'ot') {
        return columns.filter((column) => !columnsMissingOnSEWPartnerSites.includes(column));
    }

    return columns;
};

export const isGearOilCrossSellingItemRow = <T>(obj: InputRow<T, any, any>): obj is CategoryItemRow<T> =>
    (obj as any).rowData !== undefined &&
    typeof (obj as any).rowData.crossSelling?.gearOil === 'object' &&
    typeof (obj as any).rowData.crossSelling.gearOil.specificationId === 'string';

export const isECoverCrossSellingItemRow = <T>(obj: InputRow<T, any, any>): obj is CategoryItemRow<T> =>
    (obj as any).rowData !== undefined &&
    typeof (obj as any).rowData.crossSelling?.eCover === 'boolean' &&
    (obj as any).rowData.crossSelling.eCover === true;

export const eCoverNeedsConfiguration = <T>(obj: InputRow<T, any, any>): obj is CategoryItemRow<T> =>
    typeof (obj as any).crossSelling?.eCoverDialog === 'boolean' &&
    (obj as any).crossSelling.eCoverDialog === true;

export const isCableCrossSellingItemRow = <T>(obj: InputRow<T, any, any>): obj is CategoryItemRow<T> =>
    (obj as any).rowData !== undefined &&
    typeof (obj as any).rowData.crossSelling?.cable === 'boolean' &&
    (obj as any).rowData.crossSelling.cable === true;

export const selectedRowsCompareFunction = <T, G, E>(
    row: InputRow<T, G, E>,
    checkedRows: SelectableRow<T>[]
): boolean =>
    isSelectableRow(row) &&
    checkedRows.some(
        (checkedRow) =>
            isSparePart(checkedRow.rowData) &&
            isSparePart(row.rowData) &&
            checkedRow.rowData.id === row.rowData.id
    );
